/* eslint-disable @typescript-eslint/ban-types */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';

import {
  ANSWER_CONFIG_TYPE,
  AnswerConfigType,
  ORIGINAL_FORM_VALIDATION,
  QUESTION_TYPE_ID,
} from '~/common/constants';
import {
  SingleAnswerConfigProps,
  OriginalFormQuestionIdMap,
  SingleAnswerConfigAnswerProps,
} from '~/common/types';
import SortableItem from './SortableItem';
import AnswerControl from '../AnswerControl';

type Props = {
  questionIdx: number;
  totalQuestions: number;
  isPullDown?: boolean;
  isQuestionValid?: boolean;
  questionIds: Array<OriginalFormQuestionIdMap>;
  handleValidation: Function;
  handleAnswerChange: Function;
  singleAnswerConfig: SingleAnswerConfigProps;
  isValidate?: boolean;
  handleFormValidationError: Function;
};

const SimpleAnswer: React.FC<Props> = ({
  questionIdx,
  totalQuestions,
  isPullDown,
  isQuestionValid,
  questionIds,
  handleValidation,
  handleAnswerChange,
  singleAnswerConfig,
  isValidate,
  handleFormValidationError,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  const [answersListComponents, setAnswersListComponents] = useState(singleAnswerConfig.answers);

  const addAnswer = (answerType: AnswerConfigType) => {
    const newAnswersListComponents = [...answersListComponents];
    if (newAnswersListComponents.length + 1 > ORIGINAL_FORM_VALIDATION.ANSWER_MAX_SIZE) {
      handleFormValidationError(t('registration.originalForm.validationErrorModal.maxAnswer'));
      return;
    }

    const newAnswerConfig = { ...singleAnswerConfig };

    // Find index other answer and exclusive answer if exist
    const existOtherAnswer = newAnswersListComponents.findIndex(
      (answer) => answer.isOtherAnswer === true
    );

    if (answerType === ANSWER_CONFIG_TYPE.OTHER_REQUIRED) {
      if (existOtherAnswer < 0) {
        const newAnswer = {
          label: t('registration.originalForm.manual.answerLabel.otherAnswerRequired'),
          goto: '',
          isValid: true,
          isOtherAnswer: true,
        };

        newAnswersListComponents.push(newAnswer);
      }
    } else {
      const newAnswer = {
        label: '',
        goto: '',
        isValid: true,
      };

      if (existOtherAnswer >= 0) {
        newAnswersListComponents.splice(existOtherAnswer, 0, newAnswer);
      } else {
        newAnswersListComponents.push(newAnswer);
      }
    }

    setAnswersListComponents(newAnswersListComponents);
    handleAnswerChange(newAnswersListComponents, newAnswerConfig);
  };

  useEffect(() => {
    const checkInvalid = answersListComponents.find((el) => el.isValid === false);
    const checkGotoInvalid = answersListComponents.find((el) => el.isGotoValid === false);
    handleValidation(
      isQuestionValid === undefined
        ? true
        : isQuestionValid && checkInvalid === undefined && checkGotoInvalid === undefined
    );
  }, [answersListComponents]);

  useEffect(() => {
    if (isValidate !== undefined) {
      setAnswersListComponents(singleAnswerConfig.answers);
    }
  }, [singleAnswerConfig.answers]);

  useEffect(() => {
    setAnswersListComponents(singleAnswerConfig.answers);
  }, [singleAnswerConfig.gotoRequired]);

  /* istanbul ignore next */
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over?.id && active.id !== over.id) {
      const getNewAnswerListComponents = (items: SingleAnswerConfigAnswerProps[]) => {
        const jsonItems = items.map((item, idx) => idx + JSON.stringify(item));
        const activeIndex = jsonItems.indexOf(active.id as string);
        const overIndex = jsonItems.indexOf(over.id as string);

        return arrayMove(items, activeIndex, overIndex);
      };

      const updatedAnswerList = getNewAnswerListComponents(answersListComponents);
      setAnswersListComponents(updatedAnswerList);
      handleAnswerChange(updatedAnswerList, { ...singleAnswerConfig });
    }
  };

  return (
    <>
      <div>
        <DndContext
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          collisionDetection={closestCenter}
          onDragEnd={/* istanbul ignore next */ handleDragEnd}
        >
          <SortableContext
            items={answersListComponents.map((sortItem, idx) => idx + JSON.stringify(sortItem))}
            strategy={verticalListSortingStrategy}
          >
            {answersListComponents.map((item, answerIdx) => (
              <SortableItem
                key={answerIdx}
                id={answerIdx + JSON.stringify(item)}
                questionIdx={questionIdx}
                totalQuestions={totalQuestions}
                isPullDown={isPullDown}
                questionIds={questionIds}
                answerIdx={answerIdx}
                handleAnswerChange={handleAnswerChange}
                singleAnswerConfig={singleAnswerConfig}
                isValidate={isValidate}
                item={item}
                answersListComponents={answersListComponents}
                setAnswersListComponents={setAnswersListComponents}
                handleFormValidationError={handleFormValidationError}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      <AnswerControl
        questionType={
          isPullDown ? QUESTION_TYPE_ID.SINGLE_ANSWER_PULLDOWN : QUESTION_TYPE_ID.SINGLE_ANSWER
        }
        handleAddAnswer={addAnswer}
        answersComponentLength={answersListComponents.length}
      />
    </>
  );
};
export default SimpleAnswer;
